import { useState, useEffect, useMemo } from "react";
import { Box, Alert, Typography, Chip, Stack } from "@mui/material";
import {
	MaterialReactTable,
	useMaterialReactTable,
	MRT_ExpandAllButton,
	MRT_SelectCheckbox,
	MRT_Row,
	MRT_TableInstance,
	MRT_RowSelectionState,
	MRT_VisibilityState,
	MRT_GroupingState,
	MRT_ColumnOrderState,
	MRT_ExpandedState,
	MRT_PaginationState,
	MRT_ColumnDef,
} from "material-react-table";
import { Map } from "@mui/icons-material";
import { FileOptionTypes } from "../../../utilities/types";
import { Job } from "../../../api";
import { useImportTableViewModel } from "./ImportTable.vm";
import { JobsImportTableProps } from "./ImportTable.types";
import { ImportTableToolbar } from "./ImportTable.toolbar";
import { getColumnVisibilityFromCompanySettings } from "./ImportTable.helpers";
import PortalTooltip from "../../helpers/PortalTooltip";
import {
	column_Grower,
	column_Farm,
	column_Field,
	column_ReadyToSample,
	column_Deal,
	column_Density,
	column_Depth,
	column_TestPackage,
	column_FrequencyOfMicros,
	column_FieldPriority,
	column_Salesperson,
	column_SalespersonPhone,
	column_Branch,
	column_LastCrop,
	column_NextCrop,
	column_YieldGoal,
	column_EstimatedFieldReadyDate,
	column_BoundaryGeoJSON,
	column_PointsGeoJSON,
	column_ZonesGeoJSON,
	column_LabSubmissionCode,
	column_LabInstructions,
	column_GrowerPhone,
	column_SubmissionNotes,
	column_SubmitterName,
	column_SubmitterEmail,
	column_FieldId,
	column_EventId,
	column_CollectBoundary,
	column_IsTestJob,
	column_MicrosList,
	column_FieldNotes,
	column_LabName,
	column_ExternalLabId,
	column_ExternalId,
	column_ExternalFarmId,
} from "./columns";
import "./ImportTable.css";

export function JobImportTable(props: JobsImportTableProps) {
	const vm = useImportTableViewModel(props);

	// ============= MRT STATE =============
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [expanded, setExpanded] = useState<MRT_ExpandedState>(true);
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
		getColumnVisibilityFromCompanySettings(props.selectedCompany, props.user),
	);
	const [grouping, setGrouping] = useState<MRT_GroupingState>(
		props.jobs.filter((job) => !!job.importGrowerName).length > 0 ? ["grower"] : [],
	);
	const [, setColumnOrder] = useState<MRT_ColumnOrderState>(["grower", "field", "farm"]);

	// Update column visibility when company changes
	useEffect(() => {
		setColumnVisibility(getColumnVisibilityFromCompanySettings(props.selectedCompany, props.user));
	}, [props.selectedCompany, props.user]);

	// ============= COLUMNS =============
	const columns = useMemo<MRT_ColumnDef<Job>[]>(
		() => [
			// ==========================================================================================
			// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
			// ==========================================================================================
			column_Grower(vm.columnContext),
			column_Farm(vm.columnContext),
			column_Field(vm.columnContext),
			column_ReadyToSample(vm.columnContext),
			column_Deal(vm.columnContext),
			column_Density(vm.columnContext),
			column_Depth(vm.columnContext),
			column_TestPackage(vm.columnContext),
			column_FrequencyOfMicros(vm.columnContext),
			column_FieldPriority(vm.columnContext),
			column_Salesperson(vm.columnContext),
			column_SalespersonPhone(vm.columnContext),
			column_Branch(vm.columnContext),
			column_LastCrop(vm.columnContext),
			column_NextCrop(vm.columnContext),
			column_YieldGoal(vm.columnContext),
			column_EstimatedFieldReadyDate(vm.columnContext),
			column_BoundaryGeoJSON(vm.columnContext),
			column_PointsGeoJSON(vm.columnContext),
			column_ZonesGeoJSON(vm.columnContext),
			column_LabSubmissionCode(vm.columnContext),
			column_LabInstructions(vm.columnContext),
			column_GrowerPhone(vm.columnContext),
			column_SubmissionNotes(vm.columnContext),
			column_SubmitterName(vm.columnContext),
			column_SubmitterEmail(vm.columnContext),
			column_FieldId(vm.columnContext),
			column_EventId(vm.columnContext),
			column_CollectBoundary(vm.columnContext),
			column_IsTestJob(vm.columnContext),
			column_MicrosList(vm.columnContext),
			column_FieldNotes(vm.columnContext),
			column_LabName(vm.columnContext),
			column_ExternalLabId(vm.columnContext),
			column_ExternalId(vm.columnContext),
			column_ExternalFarmId(vm.columnContext),
		],
		[vm.columnContext],
	);

	// ============= TABLE CONFIGURATION =============
	const table = useMaterialReactTable({
		columns,
		data: vm.jobs, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
		enableColumnFilterModes: true,
		enableColumnOrdering: true,
		enableGrouping: true,
		enableColumnPinning: true,
		enableColumnResizing: true,
		enableColumnActions: true,
		enableColumnDragging: false,
		enableCellActions: true,
		enableRowSelection: !!props.allowMap,
		enableEditing: true,
		editDisplayMode: "table", // ('modal', 'row', 'cell', 'table', and 'custom' are also
		onCreatingRowCancel: () => vm.setValidationErrors({}),
		onEditingRowCancel: () => vm.setValidationErrors({}),
		groupedColumnMode: "remove",
		// Prevent pagination from resetting to page 1 when editing cells.
		// Without this, typing in any cell on page 2+ would jump back to page 1.
		autoResetPageIndex: false,
		onExpandedChange: (isExpanded) => {
			setExpanded(isExpanded);
		},
		enableStickyFooter: true,
		state: {
			rowSelection,
			columnVisibility,
			grouping,
			expanded,
			density: "compact",
			pagination,
		},
		onPaginationChange: (pagination) => {
			setPagination(pagination);
		},
		onRowSelectionChange: (rows) => {
			const newRowSelection = typeof rows === "function" ? rows(rowSelection) : rows;
			props.setDrawerState(Object.keys(newRowSelection).length > 0);
			setRowSelection(newRowSelection);

			// Automatically update the map when row selection changes
			if (Object.keys(newRowSelection).length > 0 && props.setDisplayBoundaries) {
				setTimeout(async () => {
					await vm.updateBoundaries(newRowSelection);
				}, 100);
			}
		},
		displayColumnDefOptions: {
			"mrt-row-select": {
				Header: ({ table }: { table: MRT_TableInstance<Job> }) => (
					<Stack direction="row" alignItems="center" spacing={1} color={"primary"} className="row-select-header">
						<PortalTooltip title="Display geodata on map">
							<label className="select-label">
								<MRT_SelectCheckbox table={table} />
								<Box ml={0.5}>
									<Map />
								</Box>
							</label>
						</PortalTooltip>
					</Stack>
				),
				Cell: ({ row, table }: { row: MRT_Row<Job>; table: MRT_TableInstance<Job> }) => (
					<Stack direction="row" alignItems="center" spacing={1} color={"primary"} className="row-select-cell">
						<PortalTooltip title="Display geodata on map">
							<label className="select-label">
								<MRT_SelectCheckbox row={row} table={table} />
								<Box ml={0.5}>
									<Map />
								</Box>
							</label>
						</PortalTooltip>
					</Stack>
				),
				size: 80,
			},
			"mrt-row-expand": {
				Header: () => (
					<Stack direction="row" alignItems="center" color={"secondary"} className="row-expand-header">
						<MRT_ExpandAllButton table={table} />
						<Box>Groups</Box>
					</Stack>
				),
				GroupedCell: ({ row, table }) => {
					const { grouping } = table.getState();
					return row.getValue(grouping[grouping.length - 1]);
				},
				enableResizing: true,
				size: 200,
			},
		},
		onColumnVisibilityChange: (column) => {
			setColumnVisibility(column);
		},
		onColumnOrderChange: (columns) => {
			setColumnOrder(columns);
		},
		onGroupingChange: (grouping) => {
			setGrouping(grouping);
		},
		muiTableBodyCellProps: ({ row }) => ({
			sx: {
				backgroundColor: row.depth === 0 ? "#d4d5d6" : "inherit",
			},
		}),
		layoutMode: "grid",
		initialState: {
			showGlobalFilter: true,
			grouping,
			pagination: {
				pageIndex: 0,
				pageSize: 20,
			},
			columnPinning: {
				left: ["grower", "field"],
				right: ["mrt-row-select"],
			},
		},
		paginationDisplayMode: "pages",
		positionToolbarAlertBanner: "bottom",
		muiSearchTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiPaginationProps: {
			color: "secondary",
			rowsPerPageOptions: [10, 15, 20, 30, 50],
			shape: "rounded",
			variant: "outlined",
		},
		renderTopToolbar: ({ table }) => (
			<ImportTableToolbar
				table={table}
				jobs={vm.jobs}
				hasValidationErrors={vm.hasValidationErrors}
				exportCSV={vm.exportCSV}
				createJobs={vm.createJobs}
			/>
		),
	});

	return (
		<div id="bulk-import-table" className="import-table">
			{/* Stats Notice */}
			{(vm.jobs.length > 0 || props.featureStats) && (
				<Alert severity="info" className="stats-alert" icon={false}>
					<Box className="stats-container">
						{/* Jobs count */}
						<Box>
							<Typography variant="body2" component="span" className="label">
								Jobs:
							</Typography>{" "}
							<Typography variant="body2" component="span">
								{vm.jobs.length}
							</Typography>
						</Box>

						{/* Feature stats */}
						{props.featureStats && (
							<Box className="feature-stats">
								<Box className="chips-container">
									{props.featureStats.boundaries > 0 && (
										<Chip label={`Fields: ${props.featureStats.boundaries}`} size="small" variant="outlined" />
									)}
									{props.featureStats.zones > 0 && (
										<Chip
											label={`Fields with ${FileOptionTypes.Zones.label}: ${props.featureStats.zones}`}
											size="small"
											variant="outlined"
										/>
									)}
									{props.featureStats.centroids > 0 && (
										<Chip
											label={`Fields with ${FileOptionTypes.Centroids.label}: ${props.featureStats.centroids}`}
											size="small"
											variant="outlined"
										/>
									)}
									{props.featureStats.cores > 0 && (
										<Chip
											label={`Fields with ${FileOptionTypes.Cores.label}: ${props.featureStats.cores}`}
											size="small"
											variant="outlined"
										/>
									)}
								</Box>
							</Box>
						)}

						{/* Validation errors */}
						{Object.keys(vm.validationErrors).length > 0 && (
							<Box className="validation-errors">
								<Typography variant="body2" component="span" className="error-label">
									Invalid:
								</Typography>
								{vm.getValidationErrorSummary().map((error, index) => (
									<Chip
										key={index}
										label={`${error.field}: ${error.count}`}
										size="small"
										color="error"
										variant="outlined"
									/>
								))}
							</Box>
						)}
					</Box>
				</Alert>
			)}
			<MaterialReactTable table={table} />
		</div>
	);
}
